<template>
  <div>
    <v-row
      class="mt-4"
      align="center"
    >
      <v-col
        cols="12"
        sm="4"
        md="3"
      >
        <v-text-field
          v-model="filter.deviceId"
          label="Device Id"
          clearable
          hide-details
          @change="reload"
        />
      </v-col>
      <v-col
        cols="12"
        sm="4"
        md="3"
      >
        <v-text-field
          v-model="filter.state"
          label="State"
          placeholder="e.g. Completed"
          clearable
          hide-details
          @change="reload"
        />
      </v-col>
      <v-col
        cols="12"
        md="6"
        class="text-md-right"
      >
        <app-btn @click="reload">
          <v-icon>mdi-refresh</v-icon> Refresh
        </app-btn>
      </v-col>
    </v-row>

    <v-divider class="mt-3" />

    <v-data-table
      :headers="headers"
      :items="items"
      :options.sync="options"
      :server-items-length="totalCount"
      :loading="loading"
      :expanded.sync="expandedRows"
      show-expand
      single-expand
      class="responsive-data-table taxi-fares-data-table"
      item-key="tripInstanceId"
      :footer-props="{
        showFirstLastPage: true,
        'items-per-page-options': [10, 15, 25, 50],
      }"
    >
      <template v-slot:[`item.data-table-expand`]="{ item, isExpanded, expand }">
        <v-btn
          icon
          small
          class="expand-toggle-btn"
          @click="expand(!isExpanded)"
        >
          <v-icon>{{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-btn>
      </template>

      <template v-slot:[`item.deviceId`]="{ item }">
        <div class="font-weight-medium">
          {{ item.deviceId }}
        </div>
        <div class="text-caption grey--text">
          Trip #{{ item.tripLocalId }}<span v-if="item.serverJobId"> &middot; Job #{{ item.serverJobId }}</span>
        </div>
      </template>

      <template v-slot:[`item.state`]="{ item }">
        <v-chip
          small
          :color="stateColor(item.state)"
          text-color="white"
        >
          {{ item.state || 'Unknown' }}
        </v-chip>
      </template>

      <template v-slot:[`item.hasTelemetryGap`]="{ item }">
        <span
          v-if="item.hasTelemetryGap"
          class="text-caption error--text telemetry-gap-badge"
        >
          <v-icon
            x-small
            color="error"
          >
            mdi-alert
          </v-icon>
          Telemetry gap
        </span>
        <span
          v-else
          class="grey--text text-caption"
        >
          -
        </span>
      </template>

      <template v-slot:[`item.effectiveTotalCents`]="{ item }">
        {{ formatCents(item.effectiveTotalCents) }}
      </template>

      <template v-slot:[`item.payableTotalCents`]="{ item }">
        {{ formatCents(item.payableTotalCents) }}
      </template>

      <template v-slot:[`item.differenceCents`]="{ item }">
        <span :class="{ 'error--text': item.differenceCents }">
          {{ formatCents(item.differenceCents) }}
        </span>
      </template>

      <template v-slot:[`item.fareSource`]="{ item }">
        {{ item.fareSource || '-' }}
      </template>

      <template v-slot:[`item.lastMessageAtUtc`]="{ item }">
        {{ formatDateTime(item.lastMessageAtUtc) }}
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <app-btn
          small
          @click="openRoute(item)"
        >
          <v-icon small>
            mdi-map-marker-path
          </v-icon> View Route
        </app-btn>
      </template>

      <template v-slot:expanded-item="{ headers: rowHeaders, item }">
        <td
          :colspan="rowHeaders.length"
          class="expanded-detail-cell"
        >
          <div class="detail-card-row">
            <div class="detail-card">
              <div class="detail-card-title">
                Meta
              </div>
              <div class="detail-line">
                <span class="detail-label">Meter Config Version</span>
                <span class="detail-value">{{ orDash(item.meterConfigVersion) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Last Message Seq</span>
                <span class="detail-value">{{ orDash(item.lastMessageSequence) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Last Tick Seq</span>
                <span class="detail-value">{{ orDash(item.lastTickSequence) }}</span>
              </div>
              <div
                v-if="item.hasTelemetryGap"
                class="detail-line"
              >
                <span class="detail-label">Missing Messages</span>
                <span class="detail-value error--text">{{ item.missingMessageFrom }}&ndash;{{ item.missingMessageTo }}</span>
              </div>
            </div>

            <div class="detail-card">
              <div class="detail-card-title">
                Device Fare
              </div>
              <div class="detail-line">
                <span class="detail-label">Device Total</span>
                <span class="detail-value">{{ formatCents(item.deviceTotalCents) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Difference</span>
                <span
                  class="detail-value"
                  :class="{ 'error--text': item.differenceCents }"
                >{{ formatCents(item.differenceCents) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Effective Total</span>
                <span class="detail-value">{{ formatCents(item.effectiveTotalCents) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Payable Total</span>
                <span class="detail-value">{{ formatCents(item.payableTotalCents) }}</span>
              </div>
            </div>

            <div class="detail-card detail-card-wide">
              <div class="detail-card-title">
                Server Fare
              </div>
              <template v-if="item.serverFare">
                <div class="detail-line">
                  <span class="detail-label">Calculated Total</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.calculatedTotalCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Payable Total</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.payableTotalCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Distance Fare</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.distanceFareCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Time Fare</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.timeFareCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">GPS Inactive Fare</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.gpsInactiveFareCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Extras / Special / Tolls</span>
                  <span class="detail-value">{{ formatCents(item.serverFare.extrasCents) }} / {{ formatCents(item.serverFare.specialFaresCents) }} / {{ formatCents(item.serverFare.tollsCents) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Odometer</span>
                  <span class="detail-value">{{ formatDistance(item.serverFare.odometerMeters) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Billable Distance</span>
                  <span class="detail-value">{{ formatDistance(item.serverFare.billableDistanceMeters) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">GPS Active / Inactive</span>
                  <span class="detail-value">{{ formatDuration(item.serverFare.gpsActiveSeconds) }} / {{ formatDuration(item.serverFare.gpsInactiveSeconds) }}</span>
                </div>
                <div class="detail-line">
                  <span class="detail-label">Last Tick At</span>
                  <span class="detail-value">{{ formatDateTime(item.serverFare.lastTickAtUtc) }}</span>
                </div>
              </template>
              <div
                v-else
                class="grey--text text-caption"
              >
                No server fare data.
              </div>
            </div>

            <div
              v-if="item.tolls"
              class="detail-card detail-card-tolls"
            >
              <div class="detail-card-title">
                Tolls
              </div>
              <div class="detail-line">
                <span class="detail-label">Total</span>
                <span class="detail-value">{{ formatCents(item.tolls.totalCents) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Charged Count</span>
                <span class="detail-value">{{ orDash(item.tolls.chargedCount) }}</span>
              </div>
              <div class="detail-line">
                <span class="detail-label">Calculated At</span>
                <span class="detail-value">{{ formatDateTime(item.tolls.calculatedAtUtc) }}</span>
              </div>

              <template v-if="tollLines(item).length">
                <div class="detail-subtitle">
                  Toll Points
                </div>
                <div
                  v-for="(line, index) in tollLines(item)"
                  :key="`${line.code || 'toll'}-${index}`"
                  class="toll-line"
                >
                  <div class="toll-line-head">
                    <span class="toll-line-name">{{ line.name || line.code || '-' }}</span>
                    <span class="toll-line-fare">{{ formatTollFare(line) }}</span>
                  </div>
                  <div class="toll-line-meta">
                    {{ tollMeta(line) }}
                  </div>
                </div>
              </template>

              <template v-if="tollUnpriced(item).length">
                <div class="detail-subtitle">
                  Unpriced
                </div>
                <div
                  v-for="(entry, index) in tollUnpriced(item)"
                  :key="`unpriced-${index}`"
                  class="toll-line-meta"
                >
                  {{ unpricedLabel(entry) }}
                </div>
              </template>

              <div
                v-if="!tollLines(item).length && !tollUnpriced(item).length"
                class="grey--text text-caption mt-2"
              >
                No toll points recorded.
              </div>
            </div>
          </div>
        </td>
      </template>
    </v-data-table>

    <v-dialog
      v-model="routeDialog"
      max-width="1400px"
      :fullscreen="$vuetify.breakpoint.smAndDown"
      @input="onRouteDialogToggle"
    >
      <v-card>
        <v-card-title>
          <span class="text-h5">Trip Route</span>
          <v-spacer />
          <v-btn
            icon
            @click="routeDialog = false"
          >
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text>
          <div
            v-if="selectedTrip"
            class="trip-summary-strip"
          >
            <div class="trip-summary-item">
              <span class="grey--text text-caption">Device</span>
              <div class="font-weight-medium">
                {{ selectedTrip.deviceId }}
              </div>
            </div>
            <div class="trip-summary-item">
              <span class="grey--text text-caption">Trip</span>
              <div class="font-weight-medium">
                #{{ selectedTrip.tripLocalId }}
              </div>
            </div>
            <div
              v-if="selectedTrip.serverJobId"
              class="trip-summary-item"
            >
              <span class="grey--text text-caption">Job Id</span>
              <div class="font-weight-medium">
                {{ selectedTrip.serverJobId }}
              </div>
            </div>
            <div class="trip-summary-item">
              <span class="grey--text text-caption">State</span>
              <div class="font-weight-medium">
                {{ selectedTrip.state || 'Unknown' }}
              </div>
            </div>
            <div class="trip-summary-item">
              <span class="grey--text text-caption">Fare Source</span>
              <div class="font-weight-medium">
                {{ selectedTrip.fareSource || '-' }}
              </div>
            </div>
            <div class="trip-summary-item">
              <span class="grey--text text-caption">Last Message</span>
              <div class="font-weight-medium">
                {{ formatDateTime(selectedTrip.lastMessageAtUtc) }}
              </div>
            </div>
          </div>

          <v-row class="mt-2">
            <v-col
              cols="12"
              md="7"
            >
              <div
                v-if="trackLoading"
                class="map-placeholder"
              >
                <v-progress-circular
                  indeterminate
                  color="primary"
                />
              </div>
              <div
                v-else-if="trackPoints.length === 0"
                class="map-placeholder"
              >
                No GPS points recorded for this trip.
              </div>
              <div
                v-show="!trackLoading && trackPoints.length > 0"
                ref="mapEl"
                class="route-map"
              />

              <div
                v-if="trackPoints.length > 0"
                class="route-endpoints mt-3"
              >
                <div>
                  <v-icon
                    small
                    color="black"
                  >
                    mdi-circle
                  </v-icon>
                  Start &middot; {{ formatDateTime(trackPoints[0].timeUtc) }}
                  <span class="grey--text">({{ trackPoints[0].latitude.toFixed(5) }}, {{ trackPoints[0].longitude.toFixed(5) }})</span>
                </div>
                <div>
                  <v-icon
                    small
                    color="amber darken-2"
                  >
                    mdi-circle
                  </v-icon>
                  End &middot; {{ formatDateTime(trackPoints[trackPoints.length - 1].timeUtc) }}
                  <span class="grey--text">({{ trackPoints[trackPoints.length - 1].latitude.toFixed(5) }}, {{ trackPoints[trackPoints.length - 1].longitude.toFixed(5) }})</span>
                </div>
              </div>
            </v-col>

            <v-col
              cols="12"
              md="5"
            >
              <div class="text-subtitle-2 mb-2 font-weight-bold">
                Fare Breakdown
              </div>
              <v-simple-table
                v-if="fareBreakdown.rows.length"
                dense
                class="fare-breakdown-table"
              >
                <thead>
                  <tr>
                    <th>Status</th>
                    <th class="text-right">
                      Time
                    </th>
                    <th class="text-right">
                      Distance
                    </th>
                    <th class="text-right">
                      Fare
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="row in fareBreakdown.rows"
                    :key="row.label"
                  >
                    <td class="nowrap">
                      {{ row.label }}
                    </td>
                    <td class="text-right">
                      {{ formatDuration(row.seconds) }}
                    </td>
                    <td class="text-right">
                      {{ formatDistance(row.meters) }}
                    </td>
                    <td class="text-right">
                      {{ formatCents(row.fareCents) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <v-simple-table
                v-if="fareBreakdown.extras.length"
                dense
                class="fare-breakdown-table mt-3"
              >
                <tbody>
                  <tr
                    v-for="row in fareBreakdown.extras"
                    :key="row.label"
                  >
                    <td>{{ row.label }}</td>
                    <td class="text-right">
                      {{ formatCents(row.fareCents) }}
                    </td>
                  </tr>
                  <tr class="font-weight-bold total-row">
                    <td>Calculated Total</td>
                    <td class="text-right">
                      {{ formatCents(selectedTrip && selectedTrip.serverFare ? selectedTrip.serverFare.calculatedTotalCents : null) }}
                    </td>
                  </tr>
                  <tr class="font-weight-bold total-row">
                    <td>Payable Total</td>
                    <td class="text-right">
                      {{ formatCents(selectedTrip ? selectedTrip.payableTotalCents : null) }}
                    </td>
                  </tr>
                </tbody>
              </v-simple-table>

              <template v-if="selectedTolls">
                <div class="text-subtitle-2 mt-4 mb-2 font-weight-bold toll-heading">
                  Toll Details
                  <span class="grey--text text-caption font-weight-regular">
                    {{ selectedTolls.chargedCount || 0 }} charged &middot; {{ formatCents(selectedTolls.totalCents) }}
                  </span>
                </div>
                <v-simple-table
                  v-if="selectedTollLines.length"
                  dense
                  class="fare-breakdown-table toll-details-table"
                >
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Selection</th>
                      <th class="text-right">
                        Fare
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="(line, index) in selectedTollLines"
                      :key="`${line.code || 'toll'}-${index}`"
                    >
                      <td>
                        <div
                          class="toll-cell-name"
                          :title="line.name || line.code"
                        >
                          {{ line.name || line.code || '-' }}
                        </div>
                        <div class="toll-cell-meta">
                          {{ tollMeta(line) }}
                        </div>
                      </td>
                      <td class="toll-cell-selection">
                        {{ line.selectionMethod || '-' }}
                      </td>
                      <td class="text-right">
                        {{ formatTollFare(line) }}
                      </td>
                    </tr>
                    <tr class="font-weight-bold total-row">
                      <td colspan="2">
                        Tolls Total
                      </td>
                      <td class="text-right">
                        {{ formatCents(selectedTolls.totalCents) }}
                      </td>
                    </tr>
                  </tbody>
                </v-simple-table>
                <div
                  v-else
                  class="grey--text text-caption"
                >
                  No toll points recorded for this trip.
                </div>

                <div
                  v-if="selectedTollUnpriced.length"
                  class="toll-unpriced mt-2"
                >
                  <span class="grey--text text-caption">Unpriced:</span>
                  <span
                    v-for="(entry, index) in selectedTollUnpriced"
                    :key="`unpriced-${index}`"
                    class="text-caption"
                  >
                    {{ unpricedLabel(entry) }}<span v-if="index < selectedTollUnpriced.length - 1">,</span>
                  </span>
                </div>

                <div
                  v-if="selectedTolls.calculatedAtUtc"
                  class="grey--text text-caption mt-2"
                >
                  Calculated at {{ formatDateTime(selectedTolls.calculatedAtUtc) }}
                </div>
              </template>

              <div
                v-if="!fareBreakdown.rows.length"
                class="grey--text text-caption"
              >
                No fare details available for this trip.
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
  export default {
    name: 'Esp32TaxiFares',

    data: () => ({
      loading: false,
      items: [],
      totalCount: 0,
      options: {},
      expandedRows: [],
      filter: {
        deviceId: undefined,
        state: undefined,
      },
      headers: [
        { text: 'Device / Trip', value: 'deviceId' },
        { text: 'State', value: 'state' },
        { text: 'Telemetry', value: 'hasTelemetryGap', sortable: false },
        { text: 'Effective Total', value: 'effectiveTotalCents' },
        { text: 'Payable Total', value: 'payableTotalCents' },
        { text: 'Difference', value: 'differenceCents' },
        { text: 'Fare Source', value: 'fareSource' },
        { text: 'Last Message', value: 'lastMessageAtUtc' },
        { text: 'Actions', value: 'actions', align: 'end', sortable: false },
      ],

      routeDialog: false,
      selectedTrip: null,
      trackLoading: false,
      trackPoints: [],
      map: null,
      mapLayers: [],
    }),

    computed: {
      fareBreakdown () {
        const fare = this.selectedTrip && this.selectedTrip.serverFare
        if (!fare) {
          return { rows: [], extras: [] }
        }

        const rows = [
          {
            label: 'GPS Active',
            seconds: fare.gpsActiveSeconds,
            meters: fare.billableDistanceMeters,
            fareCents: (fare.timeFareCents || 0) + (fare.distanceFareCents || 0),
          },
          {
            label: 'GPS Inactive',
            seconds: fare.gpsInactiveSeconds,
            meters: 0,
            fareCents: fare.gpsInactiveFareCents,
          },
        ]

        const extras = [
          { label: 'Extras', fareCents: fare.extrasCents },
          { label: 'Special Fares', fareCents: fare.specialFaresCents },
          { label: 'Tolls', fareCents: fare.tollsCents },
        ].filter(row => row.fareCents)

        return { rows, extras }
      },

      selectedTolls () {
        return (this.selectedTrip && this.selectedTrip.tolls) || null
      },

      selectedTollLines () {
        return this.tollLines(this.selectedTrip)
      },

      selectedTollUnpriced () {
        return this.tollUnpriced(this.selectedTrip)
      },
    },

    watch: {
      options: {
        handler () {
          this.load()
        },
        deep: true,
      },
    },

    created () {
      this.load()
    },

    beforeDestroy () {
      this.destroyMap()
    },

    methods: {
      reload () {
        if (this.options.page === 1) {
          this.load()
          return
        }

        this.options = { ...this.options, page: 1 }
      },

      stateColor (state) {
        switch (String(state || '').toLowerCase()) {
          case 'completed':
          case 'closed':
            return 'success'
          case 'active':
          case 'inprogress':
            return 'info'
          case 'cancelled':
          case 'failed':
            return 'error'
          default:
            return 'grey'
        }
      },

      formatDateTime (value) {
        return value ? this.$format.dateTime(value) : '-'
      },

      orDash (value) {
        return value === null || value === undefined ? '-' : value
      },

      formatCents (cents) {
        if (cents === null || cents === undefined) {
          return '-'
        }
        return `$${(Number(cents) / 100).toFixed(2)}`
      },

      formatDuration (seconds) {
        const total = Math.round(Number(seconds) || 0)
        if (!total) {
          return '0s'
        }
        const minutes = Math.floor(total / 60)
        const remSeconds = total % 60
        return minutes > 0 ? `${minutes}m ${remSeconds}s` : `${remSeconds}s`
      },

      formatDistance (meters) {
        const total = Number(meters) || 0
        if (total >= 1000) {
          return `${(total / 1000).toFixed(1)}km`
        }
        return `${Math.round(total)}m`
      },

      tollLines (item) {
        const tolls = item && item.tolls
        return (tolls && Array.isArray(tolls.lines) ? tolls.lines : [])
      },

      tollUnpriced (item) {
        const tolls = item && item.tolls
        return (tolls && Array.isArray(tolls.unpriced) ? tolls.unpriced : [])
      },

      formatTollFare (line) {
        if (!line) {
          return '-'
        }
        if (line.fareDollars !== null && line.fareDollars !== undefined) {
          return `$${Number(line.fareDollars).toFixed(2)}`
        }
        return this.formatCents(line.fareCents)
      },

      tollMeta (line) {
        if (!line) {
          return ''
        }
        const parts = []
        if (line.chargeType) {
          parts.push(line.chargeType)
        }
        if (line.selectionMethod) {
          parts.push(line.selectionMethod)
        }
        if (line.isPairedEntryExit) {
          parts.push('Paired entry/exit')
        }
        if (line.detectedAtUtc) {
          parts.push(this.formatDateTime(line.detectedAtUtc))
        }
        return parts.join(' · ')
      },

      unpricedLabel (entry) {
        if (entry === null || entry === undefined) {
          return '-'
        }
        if (typeof entry === 'string') {
          return entry
        }
        return entry.name || entry.code || '-'
      },

      async load () {
        this.loading = true
        try {
          const page = Number(this.options.page || 1)
          const pageSize = Number(this.options.itemsPerPage || 10)
          const response = await this.$http.get('taxi-fares', {
            params: {
              deviceId: this.filter.deviceId || undefined,
              state: this.filter.state || undefined,
              pageNumber: page,
              pageSize,
            },
          })

          const data = response.data || {}
          this.items = data.items || []
          this.totalCount = Number(data.totalCount || this.items.length)
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load taxi fares.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.loading = false
        }
      },

      async openRoute (item) {
        this.selectedTrip = item
        this.routeDialog = true
        this.trackPoints = []
        this.trackLoading = true

        try {
          const response = await this.$http.get(`taxi-fares/${encodeURIComponent(item.deviceId)}/${encodeURIComponent(item.tripInstanceId)}/track`)
          const points = (response.data && response.data.points) || []
          this.trackPoints = points
            .slice()
            .sort((a, b) => (a.messageSequence || 0) - (b.messageSequence || 0))

          this.$nextTick(() => {
            this.renderMap()
          })
        } catch (error) {
          this.$toast.error(
            error?.response?.data?.message || error?.message || 'Could not load trip route.',
            'Error',
            { position: 'topRight' },
          )
        } finally {
          this.trackLoading = false
        }
      },

      onRouteDialogToggle (isOpen) {
        if (!isOpen) {
          this.destroyMap()
          this.selectedTrip = null
          this.trackPoints = []
        }
      },

      renderMap () {
        if (!window.L || !this.$refs.mapEl || this.trackPoints.length === 0) {
          return
        }

        this.destroyMap()

        const latLngs = this.trackPoints.map(point => [point.latitude, point.longitude])

        this.map = window.L.map(this.$refs.mapEl)
        window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap contributors',
        }).addTo(this.map)

        const polyline = window.L.polyline(latLngs, { color: '#1976d2', weight: 4 }).addTo(this.map)
        this.mapLayers.push(polyline)

        const startMarker = window.L.circleMarker(latLngs[0], {
          radius: 7,
          color: '#000',
          fillColor: '#000',
          fillOpacity: 1,
        }).addTo(this.map).bindTooltip('Start')
        this.mapLayers.push(startMarker)

        const endMarker = window.L.circleMarker(latLngs[latLngs.length - 1], {
          radius: 7,
          color: '#ff8f00',
          fillColor: '#ff8f00',
          fillOpacity: 1,
        }).addTo(this.map).bindTooltip('End')
        this.mapLayers.push(endMarker)

        this.map.fitBounds(polyline.getBounds(), { padding: [24, 24] })

        setTimeout(() => {
          if (this.map) {
            this.map.invalidateSize()
          }
        }, 200)
      },

      destroyMap () {
        if (this.map) {
          this.map.remove()
          this.map = null
        }
        this.mapLayers = []
      },
    },
  }
</script>

<style scoped>
  .trip-summary-strip {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    padding-bottom: 12px;
    border-bottom: 1px solid #e6e9ef;
  }

  .route-map {
    width: 100%;
    height: 420px;
    border-radius: 8px;
    overflow: hidden;
  }

  .map-placeholder {
    width: 100%;
    height: 420px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f6f8;
    border-radius: 8px;
    color: #757575;
  }

  .route-endpoints {
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 0.85rem;
  }

  .taxi-fares-data-table {
    margin-top: 12px;
  }

  .telemetry-gap-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    white-space: nowrap;
  }

  .fare-breakdown-table {
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    overflow: hidden;
  }

  .fare-breakdown-table >>> .v-data-table__wrapper {
    overflow-x: hidden;
  }

  .fare-breakdown-table >>> table {
    table-layout: fixed;
    width: 100%;
  }

  .fare-breakdown-table >>> th,
  .fare-breakdown-table >>> td {
    padding: 0 8px !important;
  }

  .fare-breakdown-table >>> th {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    text-transform: uppercase;
    color: #757575;
    background: #f5f6f8;
    white-space: normal;
    line-height: 1.2;
  }

  .fare-breakdown-table >>> td {
    font-size: 0.8rem;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .fare-breakdown-table .nowrap {
    white-space: nowrap;
  }

  .fare-breakdown-table >>> .total-row td {
    border-top: 1px solid #e6e9ef;
  }

  .expand-toggle-btn {
    border: 1px solid #e0e0e0;
    background: #fafafa;
  }

  .expanded-detail-cell {
    background: #fafbfc;
    padding: 20px 24px !important;
    border-top: 1px solid #e6e9ef;
    border-bottom: 2px solid #e6e9ef;
  }

  .detail-card-row {
    display: flex;
    flex-wrap: wrap;
    gap: 16px;
    align-items: flex-start;
  }

  .detail-card {
    flex: 0 1 260px;
    max-width: 260px;
    background: #fff;
    border: 1px solid #e6e9ef;
    border-radius: 8px;
    padding: 14px 16px;
  }

  .detail-card-wide {
    flex-basis: 320px;
    max-width: 320px;
  }

  .detail-card-title {
    font-size: 0.7rem;
    font-weight: 700;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #9e9e9e;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px solid #f0f1f4;
  }

  .detail-line {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 16px;
    font-size: 0.82rem;
    padding: 4px 0;
  }

  .detail-label {
    color: #757575;
    white-space: nowrap;
  }

  .detail-value {
    font-weight: 500;
    text-align: right;
    white-space: nowrap;
  }

  .detail-card-tolls {
    flex-basis: 340px;
    max-width: 340px;
  }

  .detail-subtitle {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #9e9e9e;
    margin: 12px 0 6px;
    padding-top: 8px;
    border-top: 1px solid #f0f1f4;
  }

  .toll-line {
    padding: 4px 0;
  }

  .toll-line-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 12px;
    font-size: 0.82rem;
  }

  .toll-line-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .toll-line-fare {
    font-weight: 500;
    white-space: nowrap;
  }

  .toll-line-meta {
    font-size: 0.7rem;
    color: #9e9e9e;
  }

  .toll-heading {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
  }

  .toll-details-table >>> td {
    white-space: normal;
  }

  .toll-cell-name {
    font-size: 0.8rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .toll-cell-meta {
    font-size: 0.68rem;
    color: #9e9e9e;
  }

  .toll-cell-selection {
    font-size: 0.75rem !important;
    color: #616161;
  }

  .toll-unpriced {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  @media (max-width: 700px) {
    .detail-card,
    .detail-card-wide,
    .detail-card-tolls {
      flex-basis: 100%;
      max-width: 100%;
    }
  }
</style>
